<template>
  <div class="fixed top-4 right-4 z-50 max-w-sm">
    <TransitionGroup
      name="notification"
      tag="div"
      class="space-y-2"
    >
      <div
        v-for="notification in validNotifications"
        :key="notification.id"
        :class="[
          'p-4 rounded-lg shadow-lg border-l-4 transition-all duration-300',
          notificationClasses(notification.type)
        ]"
      >
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <component
              :is="getNotificationIcon(notification.type)"
              :class="[
                'w-5 h-5',
                notificationIconClasses(notification.type)
              ]"
            />
          </div>
          <div class="ml-3 flex-1">
            <p class="text-sm font-medium" :class="notificationTitleClasses(notification.type)">
              {{ notification.title || 'Notification' }}
            </p>
            <p v-if="notification.message" class="mt-1 text-sm" :class="notificationMessageClasses(notification.type)">
              {{ notification.message }}
            </p>
            <div v-if="notification.actions" class="mt-3 flex space-x-2">
              <button
                v-for="action in notification.actions"
                :key="action.id"
                @click="handleActionClick(action, notification)"
                class="text-xs font-medium px-3 py-1 rounded transition-colors"
                :class="actionClasses(action.type || 'primary')"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
          <div class="ml-4 flex-shrink-0">
            <button
              @click="dismissNotification(notification.id)"
              class="text-gray-400 hover:text-gray-600 dark:text-gray-300 transition-colors"
            >
              <XMarkIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

const appStore = useAppStore()

const notifications = computed(() => appStore.notifications || [])

const validNotifications = computed(() => {
  return notifications.value.filter(notification => 
    notification && 
    notification.id && 
    (notification.title || notification.message)
  )
})

const getNotificationIcon = (type) => {
  const icons = {
    success: CheckCircleIcon,
    warning: ExclamationTriangleIcon,
    error: XCircleIcon,
    info: InformationCircleIcon
  }
  return icons[type] || InformationCircleIcon
}

const notificationClasses = (type) => {
  const classes = {
    success: 'bg-green-50 border-l-green-400',
    warning: 'bg-yellow-50 border-l-yellow-400',
    error: 'bg-red-50 border-l-red-400',
    info: 'bg-blue-50 border-l-blue-400'
  }
  return classes[type] || classes.info
}

const notificationIconClasses = (type) => {
  const classes = {
    success: 'text-green-400',
    warning: 'text-yellow-400',
    error: 'text-red-400',
    info: 'text-blue-400'
  }
  return classes[type] || classes.info
}

const notificationTitleClasses = (type) => {
  const classes = {
    success: 'text-green-800',
    warning: 'text-yellow-800',
    error: 'text-red-800',
    info: 'text-blue-800'
  }
  return classes[type] || classes.info
}

const notificationMessageClasses = (type) => {
  const classes = {
    success: 'text-green-700',
    warning: 'text-yellow-700',
    error: 'text-red-700',
    info: 'text-blue-700'
  }
  return classes[type] || classes.info
}

const actionClasses = (type) => {
  const classes = {
    primary: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
    secondary: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    danger: 'bg-red-100 text-red-800 hover:bg-red-200'
  }
  return classes[type] || classes.primary
}

const dismissNotification = (id) => {
  try {
    appStore.dismissNotification(id)
  } catch (error) {
    console.error('Error dismissing notification:', error)
  }
}

const handleActionClick = (action, notification) => {
  if (action.handler) {
    action.handler(notification)
  }
  
  if (action.dismissOnClick !== false) {
    dismissNotification(notification.id)
  }
}
</script>

<style scoped>
.notification-enter-active {
  transition: all 0.3s ease-out;
}

.notification-leave-active {
  transition: all 0.3s ease-in;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}
</style>